import React, { useState } from 'react';
import {
    Box,
    Typography,
    Paper,
    Grid,
    TextField,
    Button,
    Card,
    CardContent,
    Tabs,
    Tab,
    CircularProgress,
    Alert,
    Snackbar,
    Divider,
    List,
    ListItem,
    ListItemText,
    IconButton
} from '@mui/material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { baseClient } from '../api/baseClient';
import BusinessIcon from '@mui/icons-material/Business';
import DescriptionIcon from '@mui/icons-material/Description';
import PeopleIcon from '@mui/icons-material/People';
import BarChartIcon from '@mui/icons-material/BarChart';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';

interface CompanyProfile {
    id: number;
    name: string;
    industry: string;
    size: number;
    founded_year: number;
    website: string;
    description: string;
    logo_url: string | null;
    headquarters: string;
    business_plan: {
        mission: string;
        vision: string;
        values: string[];
        goals: {
            short_term: string[];
            mid_term: string[];
            long_term: string[];
        };
        target_market: string;
        competitive_analysis: string;
        swot: {
            strengths: string[];
            weaknesses: string[];
            opportunities: string[];
            threats: string[];
        };
    };
    created_at: string;
    updated_at: string;
}

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`company-tabpanel-${index}`}
            aria-labelledby={`company-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ p: 3 }}>
                    {children}
                </Box>
            )}
        </div>
    );
}

export default function CompanyProfilePage() {
    const [tabValue, setTabValue] = useState(0);
    const [editMode, setEditMode] = useState(false);
    const [formData, setFormData] = useState<Partial<CompanyProfile>>({});
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

    const queryClient = useQueryClient();

    // Use fallback company profile data for demo
    const { data: profile, isLoading, error } = useQuery({
        queryKey: ['companyProfile'],
        queryFn: async () => {
            // Always return fallback company data for demo
            return {
                id: 1,
                name: 'EF-CRM Inc.',
                industry: 'Software & Technology',
                size: 87,
                founded_year: 2020,
                website: 'https://ef-crm.co.uk',
                description: 'Leading AI-powered CRM platform helping businesses transform their customer relationships.',
                logo_url: null,
                headquarters: 'San Francisco, CA',
                business_plan: {
                    mission: 'To revolutionize customer relationship management through AI-powered insights.',
                    vision: 'A world where every business relationship is optimized by intelligent automation.',
                    values: ['Innovation', 'Customer Success', 'Data-Driven Decisions', 'Transparency'],
                    goals: {
                        short_term: ['Expand AI capabilities', 'Improve user experience'],
                        mid_term: ['Global market expansion', 'Advanced analytics platform'],
                        long_term: ['Industry leadership', 'Complete CRM automation']
                    },
                    target_market: 'Mid to large enterprises seeking AI-powered CRM solutions',
                    competitive_analysis: 'Leading in AI integration compared to traditional CRM platforms',
                    swot: {
                        strengths: ['Advanced AI', 'User-friendly interface', 'Scalable architecture'],
                        weaknesses: ['New market presence', 'Limited integrations'],
                        opportunities: ['Growing AI market', 'Remote work trends'],
                        threats: ['Established competitors', 'Economic uncertainty']
                    }
                },
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString()
            } as CompanyProfile;
        }
    });

    // Update company profile mutation
    const updateProfileMutation = useMutation({
        mutationFn: async (updatedProfile: Partial<CompanyProfile>) => {
            const response = await baseClient.put<CompanyProfile>('/company-profiles/current', updatedProfile);
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['companyProfile'] });
            setEditMode(false);
            setSnackbar({
                open: true,
                message: 'Company profile updated successfully',
                severity: 'success'
            });
        },
        onError: (error) => {
            setSnackbar({
                open: true,
                message: `Error updating company profile: ${error instanceof Error ? error.message : 'Unknown error'}`,
                severity: 'error'
            });
        }
    });

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleEditToggle = () => {
        if (editMode) {
            // Cancel edit mode
            setFormData({});
            setEditMode(false);
        } else {
            // Enter edit mode
            if (profile) {
                setFormData({
                    name: profile.name,
                    industry: profile.industry,
                    size: profile.size,
                    founded_year: profile.founded_year,
                    website: profile.website,
                    description: profile.description,
                    headquarters: profile.headquarters
                });
            }
            setEditMode(true);
        }
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (profile) {
            updateProfileMutation.mutate(formData);
        }
    };

    const handleCloseSnackbar = () => {
        setSnackbar(prev => ({ ...prev, open: false }));
    };

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error || !profile) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">
                    Error loading company profile data. Please try again later.
                </Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3, maxWidth: 1200, margin: '0 auto' }}>
            <Typography variant="h4" sx={{ mb: 4 }}>Company Profile</Typography>

            <Grid container spacing={3}>
                {/* Company Header */}
                <Grid item xs={12}>
                    <Paper sx={{ p: 3, display: 'flex', alignItems: 'center' }}>
                        <Box sx={{
                            width: 100,
                            height: 100,
                            mr: 3,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: 'primary.main',
                            borderRadius: '50%'
                        }}>
                            {profile.logo_url ? (
                                <img
                                    src={profile.logo_url}
                                    alt={profile.name}
                                    style={{ maxWidth: '100%', maxHeight: '100%', borderRadius: '50%' }}
                                />
                            ) : (
                                <BusinessIcon sx={{ fontSize: 60, color: 'white' }} />
                            )}
                        </Box>
                        <Box>
                            <Typography variant="h5">
                                {profile.name}
                            </Typography>
                            <Typography variant="body1" color="text.secondary">
                                {profile.industry} • {profile.size} employees
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Founded {profile.founded_year} • {profile.headquarters}
                            </Typography>
                        </Box>
                        <Box sx={{ ml: 'auto' }}>
                            <Button
                                variant={editMode ? "outlined" : "contained"}
                                onClick={handleEditToggle}
                                color={editMode ? "error" : "primary"}
                            >
                                {editMode ? "Cancel" : "Edit Profile"}
                            </Button>
                        </Box>
                    </Paper>
                </Grid>

                {/* Company Tabs */}
                <Grid item xs={12}>
                    <Paper sx={{ width: '100%' }}>
                        <Tabs
                            value={tabValue}
                            onChange={handleTabChange}
                            indicatorColor="primary"
                            textColor="primary"
                            variant="fullWidth"
                        >
                            <Tab icon={<BusinessIcon />} label="Company Info" />
                            <Tab icon={<DescriptionIcon />} label="Business Plan" />
                            <Tab icon={<PeopleIcon />} label="Team" />
                            <Tab icon={<BarChartIcon />} label="Analytics" />
                        </Tabs>

                        {/* Company Info Tab */}
                        <TabPanel value={tabValue} index={0}>
                            {editMode ? (
                                <form onSubmit={handleSubmit}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                label="Company Name"
                                                name="name"
                                                value={formData.name || ''}
                                                onChange={handleInputChange}
                                                margin="normal"
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                label="Industry"
                                                name="industry"
                                                value={formData.industry || ''}
                                                onChange={handleInputChange}
                                                margin="normal"
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                label="Company Size"
                                                name="size"
                                                type="number"
                                                value={formData.size || ''}
                                                onChange={handleInputChange}
                                                margin="normal"
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                label="Founded Year"
                                                name="founded_year"
                                                type="number"
                                                value={formData.founded_year || ''}
                                                onChange={handleInputChange}
                                                margin="normal"
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                label="Website"
                                                name="website"
                                                value={formData.website || ''}
                                                onChange={handleInputChange}
                                                margin="normal"
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                label="Headquarters"
                                                name="headquarters"
                                                value={formData.headquarters || ''}
                                                onChange={handleInputChange}
                                                margin="normal"
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                label="Description"
                                                name="description"
                                                value={formData.description || ''}
                                                onChange={handleInputChange}
                                                margin="normal"
                                                multiline
                                                rows={4}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                                                <Button
                                                    type="submit"
                                                    variant="contained"
                                                    color="primary"
                                                    disabled={updateProfileMutation.isPending}
                                                >
                                                    {updateProfileMutation.isPending ? 'Saving...' : 'Save Changes'}
                                                </Button>
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </form>
                            ) : (
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <Card variant="outlined">
                                            <CardContent>
                                                <Typography variant="subtitle2" color="text.secondary">
                                                    Description
                                                </Typography>
                                                <Typography variant="body1" sx={{ mt: 1 }}>
                                                    {profile.description || 'No company description available.'}
                                                </Typography>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Card variant="outlined" sx={{ height: '100%' }}>
                                            <CardContent>
                                                <Typography variant="subtitle2" color="text.secondary">
                                                    Industry
                                                </Typography>
                                                <Typography variant="body1">
                                                    {profile.industry || 'Not specified'}
                                                </Typography>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Card variant="outlined" sx={{ height: '100%' }}>
                                            <CardContent>
                                                <Typography variant="subtitle2" color="text.secondary">
                                                    Company Size
                                                </Typography>
                                                <Typography variant="body1">
                                                    {profile.size ? `${profile.size} employees` : 'Not specified'}
                                                </Typography>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Card variant="outlined" sx={{ height: '100%' }}>
                                            <CardContent>
                                                <Typography variant="subtitle2" color="text.secondary">
                                                    Founded
                                                </Typography>
                                                <Typography variant="body1">
                                                    {profile.founded_year || 'Not specified'}
                                                </Typography>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Card variant="outlined" sx={{ height: '100%' }}>
                                            <CardContent>
                                                <Typography variant="subtitle2" color="text.secondary">
                                                    Website
                                                </Typography>
                                                <Typography variant="body1">
                                                    {profile.website ? (
                                                        <a href={profile.website} target="_blank" rel="noopener noreferrer">
                                                            {profile.website}
                                                        </a>
                                                    ) : 'Not specified'}
                                                </Typography>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Card variant="outlined" sx={{ height: '100%' }}>
                                            <CardContent>
                                                <Typography variant="subtitle2" color="text.secondary">
                                                    Headquarters
                                                </Typography>
                                                <Typography variant="body1">
                                                    {profile.headquarters || 'Not specified'}
                                                </Typography>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                </Grid>
                            )}
                        </TabPanel>

                        {/* Business Plan Tab */}
                        <TabPanel value={tabValue} index={1}>
                            <Typography variant="h6" sx={{ mb: 3 }}>Business Plan</Typography>

                            <Grid container spacing={3}>
                                <Grid item xs={12} md={6}>
                                    <Card variant="outlined">
                                        <CardContent>
                                            <Typography variant="subtitle1" sx={{ mb: 1 }}>Mission</Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {profile.business_plan?.mission || 'No mission statement defined.'}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <Card variant="outlined">
                                        <CardContent>
                                            <Typography variant="subtitle1" sx={{ mb: 1 }}>Vision</Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {profile.business_plan?.vision || 'No vision statement defined.'}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>

                                <Grid item xs={12}>
                                    <Card variant="outlined">
                                        <CardContent>
                                            <Typography variant="subtitle1" sx={{ mb: 1 }}>Values</Typography>
                                            {profile.business_plan?.values && profile.business_plan.values.length > 0 ? (
                                                <List dense>
                                                    {profile.business_plan.values.map((value, index) => (
                                                        <ListItem key={index}>
                                                            <ListItemText primary={value} />
                                                        </ListItem>
                                                    ))}
                                                </List>
                                            ) : (
                                                <Typography variant="body2" color="text.secondary">
                                                    No company values defined.
                                                </Typography>
                                            )}
                                        </CardContent>
                                    </Card>
                                </Grid>

                                <Grid item xs={12}>
                                    <Card variant="outlined">
                                        <CardContent>
                                            <Typography variant="subtitle1" sx={{ mb: 1 }}>Goals</Typography>

                                            <Typography variant="subtitle2" sx={{ mt: 2 }}>Short Term</Typography>
                                            {profile.business_plan?.goals?.short_term && profile.business_plan.goals.short_term.length > 0 ? (
                                                <List dense>
                                                    {profile.business_plan.goals.short_term.map((goal, index) => (
                                                        <ListItem key={index}>
                                                            <ListItemText primary={goal} />
                                                        </ListItem>
                                                    ))}
                                                </List>
                                            ) : (
                                                <Typography variant="body2" color="text.secondary">
                                                    No short-term goals defined.
                                                </Typography>
                                            )}

                                            <Typography variant="subtitle2" sx={{ mt: 2 }}>Mid Term</Typography>
                                            {profile.business_plan?.goals?.mid_term && profile.business_plan.goals.mid_term.length > 0 ? (
                                                <List dense>
                                                    {profile.business_plan.goals.mid_term.map((goal, index) => (
                                                        <ListItem key={index}>
                                                            <ListItemText primary={goal} />
                                                        </ListItem>
                                                    ))}
                                                </List>
                                            ) : (
                                                <Typography variant="body2" color="text.secondary">
                                                    No mid-term goals defined.
                                                </Typography>
                                            )}

                                            <Typography variant="subtitle2" sx={{ mt: 2 }}>Long Term</Typography>
                                            {profile.business_plan?.goals?.long_term && profile.business_plan.goals.long_term.length > 0 ? (
                                                <List dense>
                                                    {profile.business_plan.goals.long_term.map((goal, index) => (
                                                        <ListItem key={index}>
                                                            <ListItemText primary={goal} />
                                                        </ListItem>
                                                    ))}
                                                </List>
                                            ) : (
                                                <Typography variant="body2" color="text.secondary">
                                                    No long-term goals defined.
                                                </Typography>
                                            )}
                                        </CardContent>
                                    </Card>
                                </Grid>

                                <Grid item xs={12}>
                                    <Card variant="outlined">
                                        <CardContent>
                                            <Typography variant="subtitle1" sx={{ mb: 1 }}>SWOT Analysis</Typography>

                                            <Grid container spacing={2}>
                                                <Grid item xs={12} sm={6}>
                                                    <Typography variant="subtitle2" sx={{ mt: 1 }}>Strengths</Typography>
                                                    {profile.business_plan?.swot?.strengths && profile.business_plan.swot.strengths.length > 0 ? (
                                                        <List dense>
                                                            {profile.business_plan.swot.strengths.map((item, index) => (
                                                                <ListItem key={index}>
                                                                    <ListItemText primary={item} />
                                                                </ListItem>
                                                            ))}
                                                        </List>
                                                    ) : (
                                                        <Typography variant="body2" color="text.secondary">
                                                            No strengths defined.
                                                        </Typography>
                                                    )}
                                                </Grid>

                                                <Grid item xs={12} sm={6}>
                                                    <Typography variant="subtitle2" sx={{ mt: 1 }}>Weaknesses</Typography>
                                                    {profile.business_plan?.swot?.weaknesses && profile.business_plan.swot.weaknesses.length > 0 ? (
                                                        <List dense>
                                                            {profile.business_plan.swot.weaknesses.map((item, index) => (
                                                                <ListItem key={index}>
                                                                    <ListItemText primary={item} />
                                                                </ListItem>
                                                            ))}
                                                        </List>
                                                    ) : (
                                                        <Typography variant="body2" color="text.secondary">
                                                            No weaknesses defined.
                                                        </Typography>
                                                    )}
                                                </Grid>

                                                <Grid item xs={12} sm={6}>
                                                    <Typography variant="subtitle2" sx={{ mt: 1 }}>Opportunities</Typography>
                                                    {profile.business_plan?.swot?.opportunities && profile.business_plan.swot.opportunities.length > 0 ? (
                                                        <List dense>
                                                            {profile.business_plan.swot.opportunities.map((item, index) => (
                                                                <ListItem key={index}>
                                                                    <ListItemText primary={item} />
                                                                </ListItem>
                                                            ))}
                                                        </List>
                                                    ) : (
                                                        <Typography variant="body2" color="text.secondary">
                                                            No opportunities defined.
                                                        </Typography>
                                                    )}
                                                </Grid>

                                                <Grid item xs={12} sm={6}>
                                                    <Typography variant="subtitle2" sx={{ mt: 1 }}>Threats</Typography>
                                                    {profile.business_plan?.swot?.threats && profile.business_plan.swot.threats.length > 0 ? (
                                                        <List dense>
                                                            {profile.business_plan.swot.threats.map((item, index) => (
                                                                <ListItem key={index}>
                                                                    <ListItemText primary={item} />
                                                                </ListItem>
                                                            ))}
                                                        </List>
                                                    ) : (
                                                        <Typography variant="body2" color="text.secondary">
                                                            No threats defined.
                                                        </Typography>
                                                    )}
                                                </Grid>
                                            </Grid>
                                        </CardContent>
                                    </Card>
                                </Grid>

                                <Grid item xs={12}>
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            startIcon={<EditIcon />}
                                        >
                                            Edit Business Plan
                                        </Button>
                                    </Box>
                                </Grid>
                            </Grid>
                        </TabPanel>

                        {/* Team Tab */}
                        <TabPanel value={tabValue} index={2}>
                            <Typography variant="h6" sx={{ mb: 2 }}>Company Team</Typography>
                            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                                Manage your company team members and their roles.
                            </Typography>

                            <Box sx={{ textAlign: 'right', mb: 2 }}>
                                <Button variant="contained" startIcon={<AddIcon />}>
                                    Add Team Member
                                </Button>
                            </Box>

                            <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                                Team management features coming soon.
                            </Typography>
                        </TabPanel>

                        {/* Analytics Tab */}
                        <TabPanel value={tabValue} index={3}>
                            <Typography variant="h6" sx={{ mb: 2 }}>Company Analytics</Typography>
                            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                                View analytics and insights about your company performance.
                            </Typography>

                            <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                                Analytics features coming soon.
                            </Typography>
                        </TabPanel>
                    </Paper>
                </Grid>
            </Grid>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}


